import type { ComponentProps } from 'react';
import React from 'react';
import type { ViewProps } from 'react-native';
import type ReactNative from 'react-native';
import { StyleSheet, View } from 'react-native';

import { COLORS, SPACING, Z_INDEX } from '@garnish/constants';
import { useBreakpointHelpers } from '../../hooks';
import { IconLink } from '../Icon';
import { BodyText, DisplayText, LabelText } from '../Text';
import type { PaletteProps } from './Modal.types';

export const ModalRow = (props: ReactNative.ViewProps) => {
  const { children, style, ...restProps } = props;
  const { currentBreakpoint } = useBreakpointHelpers();
  const responsiveStyles = currentBreakpoint.isMobile
    ? styles.bodyElementMobile
    : styles.bodyElementTabletUp;

  return (
    <View style={[responsiveStyles, style]} {...restProps}>
      {children}
    </View>
  );
};

const iconColors = {
  secondary: COLORS.WHITE,
  primary: undefined,
};

export const ModalCloseBtn = (
  props: Omit<ComponentProps<typeof IconLink>, 'name' | 'palette'> &
    PaletteProps,
) => {
  const { palette, ...rest } = props;
  const { currentBreakpoint } = useBreakpointHelpers();
  const responsiveStyles = currentBreakpoint.isMobile
    ? styles.closeBtnWrapperMobile
    : styles.closeBtnWrapperTabletUp;

  const paletteSpecificColor = palette ? iconColors[palette] : undefined;

  return (
    <ModalRow
      style={[styles.closeBtnWrapper, responsiveStyles, props.style]}
      testID="sg-modal-close-btn"
    >
      <IconLink name="IconClose" color={paletteSpecificColor} {...rest} />
    </ModalRow>
  );
};

export const ModalHeadline = (
  props: ComponentProps<typeof DisplayText> & PaletteProps,
) => {
  const { children, palette, style, ...restProps } = props;
  const { currentBreakpoint } = useBreakpointHelpers();
  const textSize = currentBreakpoint.isMobile ? 4 : 3;

  const paletteSpecificTextStyles = palette
    ? paletteHeadingTextStyles[palette]
    : undefined;

  return (
    <ModalRow>
      <DisplayText
        size={textSize}
        style={[styles.headline, style, paletteSpecificTextStyles]}
        {...restProps}
      >
        {children}
      </DisplayText>
    </ModalRow>
  );
};

export const ModalHeader = ({
  onBack,
  children,
  onClose,
}: {
  readonly onBack?: () => void;
  readonly children?: React.ReactNode;
  readonly onClose: () => void;
}) => {
  const { currentBreakpoint } = useBreakpointHelpers();

  return (
    <View style={styles.header}>
      {onBack ? (
        <IconLink
          style={
            currentBreakpoint.isMobile
              ? styles.headerMarginMobile
              : styles.headerMarginTabletUp
          }
          name="IconArrowLeft"
          onPress={onBack}
          accessibilityHint=""
          accessibilityLabel="back"
          accessibilityRole="imagebutton"
        />
      ) : (
        <View
          style={[
            styles.emptyIcon,
            currentBreakpoint.isMobile
              ? styles.headerMarginMobile
              : styles.headerMarginTabletUp,
          ]}
        />
      )}
      {children}
      <IconLink
        style={
          currentBreakpoint.isMobile
            ? styles.headerMarginMobile
            : styles.headerMarginTabletUp
        }
        name="IconClose"
        onPress={onClose}
        accessibilityHint=""
        accessibilityLabel="close"
        accessibilityRole="imagebutton"
      />
    </View>
  );
};

export const ModalFloatingCloseBtn = (props: ModalFloatingCloseBtnProps) => {
  const { style, onPress } = props;
  const buttonStyles = [styles.closeBtnFloating, style];

  return (
    <IconLink
      testID="sg-modal-close-btn"
      style={buttonStyles}
      name="IconClose"
      onPress={onPress}
      accessibilityHint=""
      accessibilityLabel="close"
      accessibilityRole="imagebutton"
    />
  );
};

export const ModalSubhead = (
  props: ComponentProps<typeof LabelText> & PaletteProps,
) => {
  const { children, palette, ...restProps } = props;

  const paletteSpecificTextStyles = palette
    ? paletteSubheadTextStyles[palette]
    : undefined;

  return (
    <ModalRow>
      <LabelText
        size={2}
        style={[styles.subhead, paletteSpecificTextStyles]}
        {...restProps}
      >
        {children}
      </LabelText>
    </ModalRow>
  );
};

export const ModalBodyText = (
  props: ComponentProps<typeof BodyText> & PaletteProps,
) => {
  const { children, ...restProps } = props;
  const { currentBreakpoint } = useBreakpointHelpers();
  const textSize = currentBreakpoint.isMobile ? 4 : 3;

  return (
    <ModalRow>
      <BodyText size={textSize} style={styles.bodyText} {...restProps}>
        {children}
      </BodyText>
    </ModalRow>
  );
};

export const ModalFooter = (props: ModalFooterProps) => {
  const { children, style, withoutTopBorder, ...restProps } = props;
  const { currentBreakpoint } = useBreakpointHelpers();
  const responsiveStyles = currentBreakpoint.isMobile
    ? styles.footerMobile
    : styles.footerTabletUp;

  return (
    <ModalRow
      style={[
        responsiveStyles,
        withoutTopBorder
          ? styles.footerWithoutBorder
          : styles.footerWithTopBorder,
        style,
      ]}
      {...restProps}
    >
      {children}
    </ModalRow>
  );
};

//
// ─── TYPES ──────────────────────────────────────────────────────────────────────
//

type ModalFooterProps = ReactNative.ViewProps & {
  readonly withoutTopBorder?: boolean;
};

type ModalFloatingCloseBtnProps = {
  readonly style?: ViewProps['style'];
  readonly onPress: () => void;
};

//
// ─── STYLES ──────────────────────────────────────────────────────────────────────
//

const styles = StyleSheet.create({
  closeBtnWrapper: {
    alignItems: 'flex-end',
    paddingBottom: SPACING.medium,
  },
  closeBtnWrapperMobile: {
    paddingTop: SPACING.medium,
  },
  closeBtnWrapperTabletUp: {
    paddingTop: SPACING.large,
  },
  closeBtnFloating: {
    position: 'absolute',
    right: 0,
    top: 0,
    zIndex: Z_INDEX.modal,
    margin: SPACING.medium,
  },

  // ========================

  headline: {
    paddingBottom: SPACING.medium,
  },
  headlineSecondary: {
    color: COLORS.WHITE,
  },

  // ========================

  header: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  headerMarginMobile: {
    margin: SPACING.medium,
  },
  headerMarginTabletUp: {
    margin: SPACING.large,
  },

  // ========================

  subhead: {
    textTransform: 'uppercase',
    paddingBottom: SPACING.medium,
  },
  subheadSecondary: {
    color: COLORS.WHITE,
  },

  // ========================

  bodyText: {
    paddingBottom: SPACING.medium,
  },

  // ========================

  bodyElementMobile: {
    paddingHorizontal: SPACING.medium,
  },
  bodyElementTabletUp: {
    paddingHorizontal: SPACING.large,
  },

  // ========================

  footerMobile: {
    paddingVertical: SPACING.medium,
  },
  footerTabletUp: {
    paddingVertical: SPACING.large,
  },
  footerWithTopBorder: {
    borderTopWidth: 1,
    borderTopColor: COLORS.LIGHT,
  },
  footerWithoutBorder: {
    paddingTop: 0,
  },
  emptyIcon: {
    height: SPACING.large,
    width: SPACING.large,
  },
});

//
// ─── PALETTE STYLES ──────────────────────────────────────────────────────────────────────
//

const paletteHeadingTextStyles = {
  primary: undefined,
  secondary: styles.headlineSecondary,
};

const paletteSubheadTextStyles = {
  primary: undefined,
  secondary: styles.subheadSecondary,
};
